// ---------- small helpers ----------
const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";
const norm = (s) => (s ?? "").trim();
const upper = (s) => norm(s).toUpperCase();

function normalizeEnrollment(csize) {
	const parsed = Number(csize);
	return Number.isFinite(parsed) ? parsed : null;
}

function sizeGroupFromEnrollment(csize) {
	// Accept numeric strings too, because some row updates can leave csize stringly typed.
	const enrollment = normalizeEnrollment(csize);
	if (!Number.isFinite(enrollment) || enrollment <= 0) return "NONE";
	if (enrollment < 10) return "SMALL";
	return "LARGE";
}

export function courseHasNonTAInstructors(course) {
	return Array.isArray(course?.roles) && course.roles.some((role) => upper(role.class_role) !== "TA");
}

export function isTAOnlyBlockOption(blockOption) {
	if (!blockOption) return false;
	return blockOption.forPI === false || upper(blockOption.instRole) === "TA";
}

// "TA-only" means *every* instructor is TA.
// Anything else is "PI required".
function instGroupFromRoles(roles = []) {
	if (!roles.length) return "UNKNOWN";
	const allTA = roles.every((r) => upper(r.class_role) === "TA");
	return allTA ? "TA_ONLY" : "PI_REQUIRED";
}

function shouldAutoAssignForm(row) {
	// matches your intent: only assign if empty OR not excluded sentinel
	if (upper(row.form) === "EXCL") return false;
	return isBlank(row.form);
}

// courseKey for grouping counterparts / parent detection
function courseKey(row) {
	return `${upper(row.subject_id)}|${upper(row.cnumber)}`;
}

function parseCourseToken(token, knownSubjects = []) {
	const normalizedToken = upper(token).replace(/\s+/g, " ").trim();
	if (!normalizedToken) return null;

	const matchedSubject = knownSubjects.find((subject) => {
		const normalizedSubject = upper(subject).replace(/\s+/g, " ").trim();
		return (
			normalizedToken === normalizedSubject ||
			normalizedToken.startsWith(`${normalizedSubject} `)
		);
	});

	if (matchedSubject) {
		const subject = upper(matchedSubject).replace(/\s+/g, " ").trim();
		const remainder = normalizedToken.slice(subject.length).trim();
		const [cnumber, ctype] = remainder.split(/\s+/).filter(Boolean);

		if (!cnumber) {
			return null;
		}

		return {
			subject,
			cnumber: upper(cnumber),
			ctype: ctype ? upper(ctype) : null,
		};
	}

	const regexMatch = normalizedToken.match(/^(.+?)\s+([0-9][A-Z0-9]*)(?:\s+([A-Z]+))?(?:\s+\S+)?$/);
	if (!regexMatch) {
		return null;
	}

	return {
		subject: upper(regexMatch[1]),
		cnumber: upper(regexMatch[2]),
		ctype: regexMatch[3] ? upper(regexMatch[3]) : null,
	};
}

// parse BLOCK_IDS.course like "ENGL 103 LEC, ENGL 205"
function parseCourseList(courseStr, knownSubjects = []) {
	return norm(courseStr)
		.split(",")
		.map((c) => c.trim())
		.filter(Boolean)
		.map((token) => parseCourseToken(token, knownSubjects))
		.filter(Boolean);
}

// match: subject+cnumber required, ctype optional
function courseOverrideMatches(row, courseRule) {
	if (upper(row.subject_id) !== courseRule.subject) return false;
	if (!upper(row.cnumber).includes(courseRule.cnumber)) return false;
	if (courseRule.ctype && !upper(row.ctype).includes(courseRule.ctype)) return false;
	return true;
}

// BLOCK_IDS.ctype matching:
// - "LEC/LAB" => row.ctype in {LEC, LAB}
// - "LEC:LAB/SEM" => depends on *context* (counterparts or parent relationship)
function parseCtypePattern(patternRaw) {
	const pattern = upper(patternRaw);
	const [basePart, childPart] = pattern.split(":").map((p) => p.trim());

	const baseSet = new Set(
		basePart
			.split("/")
			.map((x) => x.trim())
			.filter(Boolean),
	);
	const childSet = childPart
		? new Set(
				childPart
					.split("/")
					.map((x) => x.trim())
					.filter(Boolean),
			)
		: null;

	return { pattern, hasColon: !!childSet, baseSet, childSet };
}

// ---------- build context ----------
export function buildAssignmentContext(rowsForTable, blockIdsByDept) {
	const rows = rowsForTable ?? [];
	const blocks = blockIdsByDept ?? [];
	const knownSubjects = [...new Set(rows.map((row) => upper(row.subject_id)).filter(Boolean))].sort(
		(a, b) => b.length - a.length,
	);

	// 1) course structure: for each (SUBJECT|CNUMBER), which ctypes exist?
	const courseCtypes = new Map(); // key -> Set(ctype)
	for (const r of rows) {
		const key = courseKey(r);
		if (!courseCtypes.has(key)) courseCtypes.set(key, new Set());
		courseCtypes.get(key).add(upper(r.ctype));
	}

	// 2) partition blocks + pre-parse expensive stuff
	const courseBlocks = [];
	const nonCourseBlocks = [];

	for (const b of blocks) {
		const course = norm(b.course);
		const entry = {
			...b,
			blkid: norm(b.blkid),
			origin: upper(b.origin),
			status: upper(b.status),
			csize: upper(b.csize), // SMALL | LARGE | EITHER
			inst_role: upper(b.inst_role), // TA | PI (in your simplified model)
			ctype: upper(b.ctype),
			_courseRules: course ? parseCourseList(course, knownSubjects) : [],
			_ctypePattern: parseCtypePattern(b.ctype),
		};

		if (entry._courseRules.length) courseBlocks.push(entry);
		else nonCourseBlocks.push(entry);
	}

	// 3) further group non-course blocks
	const basic = nonCourseBlocks.filter((b) => b.origin === "BASIC");
	const custom = nonCourseBlocks.filter((b) => b.origin === "CUSTOM" && b.status !== "O");

	// you can add “EITHER basic” etc if that exists in your data model
	const bySize = (arr, size) => arr.filter((b) => b.csize === size);

	return {
		rows,
		courseCtypes,
		blocks: { courseBlocks, basic, custom },
		buckets: {
			basicSmall: bySize(basic, "SMALL"),
			basicLarge: bySize(basic, "LARGE"),
			customSmall: bySize(custom, "SMALL"),
			customLarge: bySize(custom, "LARGE"),
			customEither: bySize(custom, "EITHER"),
		},
	};
}

function desiredCtypeKey(row, ctx) {
	const ctype = upper(row.ctype);
	const ctypes = ctx.courseCtypes.get(courseKey(row)) ?? new Set();

	const hasLEC = ctypes.has("LEC");
	const hasLAB = ctypes.has("LAB");
	const hasSEM = ctypes.has("SEM");

	// Interpretation A (matches your existing code): when row is LEC, colon blocks
	// represent "LEC with counterparts present".
	if (ctype === "LEC") {
		if (hasLAB && hasSEM) return "LEC:LAB/SEM";
		if (hasLAB) return "LEC:LAB";
		if (hasSEM) return "LEC:SEM";
		return "LEC";
	}

	// Interpretation B (matches your DB description): "LEC:LAB" means
	// this LAB has a parent LEC.
	if (ctype === "LAB" && hasLEC) return "LEC:LAB";
	if (ctype === "SEM" && hasLEC) return "LEC:SEM";
	if (ctype === "CLN" && hasLEC) return "LEC:CLN";

	return ctype; // fallback: LAB/SEM/CLN/etc
}

function pickFirst(list) {
	return list && list.length ? list[0] : null;
}

function matchesInstRoleRequirement(row, block) {
	const group = instGroupFromRoles(row.roles);
	if (group === "PI_REQUIRED") return block.inst_role === "PI";
	if (group === "TA_ONLY") return block.inst_role === "TA" || block.inst_role === "PI"; // your call
	return true; // UNKNOWN: let it pass, or force PI, your call
}

function ctypeMatches(row, block, desiredKey) {
	// simplest: exact match wins
	if (block._ctypePattern.pattern === desiredKey) return true;

	// if block is multi-ctype "LEC/LAB"
	if (!block._ctypePattern.hasColon) {
		return block._ctypePattern.baseSet.has(upper(row.ctype));
	}

	// if block has colon, allow matching on computed desiredKey
	return block._ctypePattern.pattern === desiredKey;
}

function chooseFromCandidateBuckets(row, ctx) {
	const sizeGroup = sizeGroupFromEnrollment(row.csize);
	const desiredKey = desiredCtypeKey(row, ctx);

	// choose candidate buckets by size with your priority
	const candidates =
		sizeGroup === "LARGE"
			? [...ctx.buckets.customLarge, ...ctx.buckets.customEither, ...ctx.buckets.basicLarge]
			: sizeGroup === "SMALL"
				? [...ctx.buckets.customSmall, ...ctx.buckets.customEither, ...ctx.buckets.basicSmall]
				: [...ctx.buckets.customEither, ...ctx.buckets.basicLarge, ...ctx.buckets.basicSmall];

	// filter by inst-role policy first
	const roleFiltered = candidates.filter((b) => matchesInstRoleRequirement(row, b));

	// then ctype match (prefer exact desiredKey)
	const exact = roleFiltered.filter((b) => upper(b.ctype) === desiredKey);
	if (exact.length) return exact[0];

	const matched = roleFiltered.filter((b) => ctypeMatches(row, b, desiredKey));
	if (matched.length) return matched[0];

	// final fallback (this is where you put “3SPOT/3STA/3OPN” style rules)
	return pickFirst(roleFiltered);
}

function applyCourseOverride(row, ctx) {
	for (const block of ctx.blocks.courseBlocks) {
		for (const rule of block._courseRules) {
			if (courseOverrideMatches(row, rule)) return block;
		}
	}
	return null;
}

function applyDeptOverrides(row, ctx) {
	const dept = Number(row.dept);

	// French depts (your hard-coded ones)
	if (dept === 27 || dept === 118) {
		const sizeGroup = sizeGroupFromEnrollment(row.csize);
		if (sizeGroup === "SMALL") {
			const b = ctx.buckets.basicSmall.find((x) => x.blkid === "3CSJO");
			if (b) return b;
		} else if (sizeGroup === "LARGE") {
			const b = ctx.buckets.basicLarge.find((x) => x.blkid === "3CSJ");
			if (b) return b;
		}
	}

	// Nursing special casing (keep it quarantined here)
	if (dept === 79) {
		// you can keep your existing nursing mapping logic;
		// for now, I’ll do a “same-shape” mapping.
		const desired = desiredCtypeKey(row, ctx);

		// Example mapping (matches your existing strings)
		if (desired === "LEC:LAB" && upper(row.ctype) === "LAB") return { blkid: "1NURS2LAB" };
		if (desired === "LEC:CLN" && upper(row.ctype) === "CLN") return { blkid: "1NURS2CLN" };

		if (upper(row.ctype) === "CLN") return { blkid: "1NURS1CLN" };
		if (upper(row.ctype) === "LAB") return { blkid: "1NURS1LAB" };
		if (upper(row.ctype) === "LEC") return { blkid: "1NURS1LEC" };
		if (upper(row.ctype) === "SEM") return { blkid: "3SPOT" };
	}

	return null;
}

export function suggestRow(row, ctx) {
	// never mutate input
	const next = { ...row };

	// evaluate caution flag (your last chunk)
	const shouldCaution =
		((next.roles && next.roles.length < 1) || next.excluded || next.csize === 0) &&
		next.evaluate !== "No";

	if (shouldCaution) next.evaluate = "Caution";
	else if (!next.evaluate) next.evaluate = "";

	// form assignment
	if (!shouldAutoAssignForm(next)) return next;

	// 1) course override
	const courseBlock = applyCourseOverride(next, ctx);
	if (courseBlock?.blkid) {
		next.form = courseBlock.blkid;
		return next;
	}

	// 2) dept overrides
	const deptBlock = applyDeptOverrides(next, ctx);
	if (deptBlock?.blkid) {
		next.form = deptBlock.blkid;
		return next;
	}

	// 3) general selection
	const chosen = chooseFromCandidateBuckets(next, ctx);
	if (chosen?.blkid) next.form = chosen.blkid;

	return next;
}

export function suggestRows(rowsForTable, blockIdsByDept) {
	const ctx = buildAssignmentContext(rowsForTable, blockIdsByDept);
	return ctx.rows.map((r) => suggestRow(r, ctx));
}

/**
 * Determines if a block option should be disabled for a given class.
 * Used by BlockIdSelect component.
 *
 * @param {Object} params
 * @param {Object} params.blockOption - The block option object with forSmallCourse property
 * @param {Object|null} params.selectedClass - The currently selected class
 * @param {boolean} params.inBatch - Whether we're in batch mode
 * @param {boolean} params.anySmallCourse - In batch mode, whether any selected course is small
 * @param {Array} params.rows - All rows (used to check for nursing)
 * @returns {boolean} - True if the option should be disabled
 */
export function isBlockDisabledForClass({
	blockOption,
	selectedClass,
	inBatch,
	anySmallCourse,
	selectedBulkClassIds,
	rows,
}) {
	// Nursing department (79) - nothing is disabled
	const isNursing =
		(selectedClass && selectedClass.dept === 79) || rows?.some((row) => row.dept === 79);

	const selectedCourses = inBatch
		? (rows ?? []).filter((row) => selectedBulkClassIds?.includes(row.classid))
		: selectedClass
			? [selectedClass]
			: [];
	const hasNonTAs = selectedCourses.some(courseHasNonTAInstructors);

	if (isNursing) return false;

	const sizeGroup = selectedClass ? sizeGroupFromEnrollment(selectedClass.csize) : null;

	// In batch mode, use anySmallCourse to determine size group
	if (inBatch) {
		const batchSizeGroup = anySmallCourse ? "SMALL" : "LARGE";
		if (batchSizeGroup === "SMALL" && blockOption.forSmallCourse === false) return true;
		if (batchSizeGroup === "LARGE" && blockOption.forSmallCourse === true) return true;
		// Additionally, enforce TA-only blocks if any selected course has non-TAs
		if (hasNonTAs && isTAOnlyBlockOption(blockOption)) return true;
		return false;
	}

	// Single class selection
	if (selectedClass) {
		if (sizeGroup === "SMALL" && blockOption.forSmallCourse === false) return true;
		if (sizeGroup === "LARGE" && blockOption.forSmallCourse === true) return true;
		if (hasNonTAs && isTAOnlyBlockOption(blockOption)) return true;
	}

	return false;
}
